import { ref, watch, type MaybeRefOrGetter, toValue } from "vue";
import { useUrlSearch } from "./useUrlSearch";
import { applySortKeys, toTableOptions, useUrlSort } from "./useUrlSort";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";

/** Default page size for list/table filters. Kept here (not in usePatient) to avoid a
 * queries → useOwnedFilterRefs → useTableFilter → usePatient → queries cycle. */
export const defaultPageSize = 20;

/** A header (subset) that can declare the backend ordering field for its column. */
type TSortableHeader = { key: string; sortKey?: string };

/**
 * URL-backed table filters as a **writable** ref.
 *
 * Search + sort are synced from the URL into the ref (so `PageHeader` / column sort keep
 * working). Everything else is free for the page to mutate on `filters.value` — date ranges,
 * selects, tab extras — without rebuilding a computed `initial`.
 *
 * `defaults` / `initial` are merged when they change, but URL sync alone does not re-apply
 * them, so field mutations on `filters` survive search/sort updates.
 */
export const useTableFilter = <T extends DefaultPaginatedFilters>(
  listenOff: boolean,
  defaults: MaybeRefOrGetter<Partial<T>> = {},
  useQuery: boolean = false,
  overrideSearch?: { value: MaybeRefOrGetter<string | null>; use: boolean },
  /**
   * When supplied, URL `ordering` stores backend fields (`sortKey`) while the table's
   * `options.sortBy` stays on column keys — and the request filters get `applySortKeys`.
   */
  headers?: MaybeRefOrGetter<readonly TSortableHeader[]>
) => {
  const { searchValue } = useUrlSearch(listenOff);
  const { sortBy } = useUrlSort(undefined, listenOff, headers);

  const filters = ref<Partial<T>>({
    page_size: defaultPageSize,
  } as Partial<T>);

  // `options` keeps the shape the data table's `v-model:options` expects, but its
  // `sortBy` is now backed by the URL (`?ordering=`) so column sorting is shareable
  // and survives reloads. Writing `options` (via the table) forwards to the URL.
  const options = toTableOptions(sortBy);

  watch(
    () => toValue(defaults),
    (seed) => {
      if (seed) {
        Object.assign(filters.value, seed);
      }
      if (filters.value.page_size == null) {
        filters.value.page_size = defaultPageSize;
      }
    },
    { immediate: true, deep: true }
  );

  watch(
    [
      searchValue,
      sortBy,
      () => (overrideSearch?.use ? toValue(overrideSearch.value) : undefined),
      () => (headers ? toValue(headers) : undefined),
    ],
    () => {
      const hs = headers ? toValue(headers) : undefined;
      const searchOrQuery = overrideSearch?.use ? toValue(overrideSearch.value) : searchValue.value;

      if (useQuery) {
        filters.value.query = searchOrQuery as Partial<T>["query"];
        delete filters.value.search;
      } else {
        filters.value.search = searchOrQuery as Partial<T>["search"];
        delete filters.value.query;
      }

      // Table arrows need column keys; the API needs backend fields when headers declare
      // a `sortKey` (e.g. `patient` → `last_name`).
      filters.value.sortBy = (
        hs ? applySortKeys(sortBy.value, hs) : sortBy.value
      ) as Partial<T>["sortBy"];
    },
    { immediate: true }
  );

  return {
    filters,
    options,
  };
};

export const useBasicFilters = <T extends DefaultPaginatedFilters>(defaults?: Partial<T>) => {
  return ref<Partial<T>>({
    search: "",
    page_size: defaultPageSize,
    ...defaults,
  } as Partial<T>);
};
